import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import {
  Box,
  StackLayout,
  UtilityText,
  Button,
  GDSIconSource,
  GDSHeadingLevel,
  Divider,
  ParagraphText,
  Image,
} from '@leagueplatform/genesis-core';
import {
  isLinkAction,
  MasonryDeeplinkAction,
} from '@leagueplatform/masonry-renderers';
import { useUniqueId } from '@leagueplatform/web-common';
import { InternalOrExternalLink as Link } from '@leagueplatform/web-common-components';
import { SpendingAccountCard } from '#components/spending-account-card/spending-account-card.component.tsx';
import { SectionHeaderWithIcon } from '#components/section-header-with-icon/section-header-with-icon.component.tsx';
import {
  buttonIndicatorMap,
  ButtonIndicatorType,
} from '#constants/indicator-map.constants.ts';

export const NODE_TYPE_SPENDING_ACCOUNT_WIDGET = 'spendingAccountWidget';

interface Icon {
  icon?: GDSIconSource | string;
  description?: string;
}

interface SpendingAccountCardType {
  icon: Icon;
  title?: string;
  amount?: string;
  ctaText?: string;
  trailingIcon?: Icon;
}

interface ButtonType {
  label: string;
  indicator?: ButtonIndicatorType;
}

type SpendingAccountWidgetProps = {
  sponsorLogo?: {
    description?: string;
    height?: string;
    url: string;
    width?: string;
  };
  headingLeadingIcon?: Icon;
  heading: string;
  headingLevel?: GDSHeadingLevel;
  description: string;
  subtext?: string;
  ctaType: 'button' | 'spendingAccountCard';
  isCTALabelGeneric: boolean;
  hasEndDivider?: boolean;
  spendingAccountCard?: SpendingAccountCardType;
  button?: ButtonType;
};

export type SpendingAccountWidgetNodeActions = {
  onClick?: MasonryEngineNodeAction;
  onLoad?: MasonryEngineNodeAction;
};

type SpendingAccountWidgetNode = MasonryEngineNode<
  typeof NODE_TYPE_SPENDING_ACCOUNT_WIDGET,
  SpendingAccountWidgetProps,
  SpendingAccountWidgetNodeActions,
  {}
>;

export type SpendingAccountWidgetRendererProps =
  MasonryEngineNodeRendererProps<SpendingAccountWidgetNode>;

export function SpendingAccountWidgetRenderer({
  sponsorLogo,
  headingLeadingIcon,
  heading,
  headingLevel = '2',
  description,
  subtext,
  ctaType,
  isCTALabelGeneric,
  hasEndDivider = false,
  spendingAccountCard,
  button,
  onClick,
}: SpendingAccountWidgetRendererProps) {
  const invokeAction = useMasonryEngineActionEmitter();
  const onClickDeeplinkAction = onClick as MasonryDeeplinkAction | undefined;
  const uniqueId = useUniqueId({
    prefix: 'spending-account-widget',
  });
  // IDs for accessibility
  const headingId = `${uniqueId}-heading`;

  const isLink = React.useMemo(
    () => isLinkAction(onClickDeeplinkAction),
    [onClickDeeplinkAction],
  );

  const href = onClickDeeplinkAction?.payload.url;

  // Handle click events
  const handleOnClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (onClick) {
        invokeAction({
          event,
          ...onClick,
        });
      }
    },
    [invokeAction, onClick],
  );

  return (
    <StackLayout as="section" aria-labelledby={headingId} spacing="$oneAndHalf">
      {sponsorLogo && (
        <Box>
          <Image
            src={sponsorLogo.url}
            alt={sponsorLogo.description ?? ''}
            width={sponsorLogo.width}
            height={sponsorLogo.height}
            loading="lazy"
            css={{ maxWidth: '100%' }}
          />
        </Box>
      )}

      <StackLayout spacing="$oneAndHalf">
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <SectionHeaderWithIcon
            heading={heading}
            headingLevel={headingLevel}
            icon={headingLeadingIcon}
            id={headingId}
          />
        </Box>

        {description && (
          <Box>
            <ParagraphText>{description}</ParagraphText>
          </Box>
        )}

        {subtext && (
          <UtilityText size="sm" color="subdued">
            {subtext}
          </UtilityText>
        )}

        {ctaType === 'spendingAccountCard' && spendingAccountCard && (
          <SpendingAccountCard
            accountName={heading}
            amount={spendingAccountCard.amount}
            ctaText={spendingAccountCard.ctaText}
            href={onClickDeeplinkAction?.payload.url}
            icon={spendingAccountCard.icon}
            isLink={isLink}
            onClick={handleOnClick}
            title={spendingAccountCard.title}
            trailingIcon={spendingAccountCard.trailingIcon}
          />
        )}

        {ctaType === 'button' && button && (
          <Box>
            <Button
              size="medium"
              priority="secondary"
              onClick={handleOnClick}
              indicator={
                button.indicator
                  ? buttonIndicatorMap[button.indicator]
                  : undefined
              }
              id={isCTALabelGeneric ? `${headingId}-cta` : undefined}
              aria-labelledby={
                isCTALabelGeneric ? `${headingId}-cta ${headingId}` : undefined
              }
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(isLink && {
                as: Link,
                href,
              })}
            >
              {button.label}
            </Button>
          </Box>
        )}
      </StackLayout>

      {hasEndDivider && <Divider />}
    </StackLayout>
  );
}
